import React, { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";

export const AdminPayouts = () => {
  const [payouts, setPayouts] = useState([]);
  const [deciding, setDeciding] = useState(null);
  const [form, setForm] = useState({ status: "approved", tx_id: "", admin_note: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/payouts").then((r) => setPayouts(r.data || []));
  useEffect(() => { load(); }, []);

  const decide = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/admin/payouts/${deciding.id}`, form);
      toast.success(`Payout ${form.status}. Trader emailed.`);
      setDeciding(null);
      setForm({ status: "approved", tx_id: "", admin_note: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-payouts-page">
      <div>
        <h1 className="font-display text-3xl font-bold">Payouts</h1>
        <p className="text-[#8a8a8a] text-sm mt-1">Review and process payout requests.</p>
      </div>
      <div className="glass rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] tracking-widest text-[#8a8a8a] uppercase border-b border-[#ff7a00]/15">
              <th className="text-left p-4">Date</th>
              <th className="text-left p-4">Trader</th>
              <th className="text-right p-4">Amount</th>
              <th className="text-left p-4">Currency</th>
              <th className="text-left p-4">Wallet</th>
              <th className="text-left p-4">Status</th>
              <th className="text-right p-4">Action</th>
            </tr>
          </thead>
          <tbody>
            {payouts.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-[#8a8a8a]">No payouts yet.</td></tr>}
            {payouts.map((p) => (
              <tr key={p.id} className="border-b border-[#ff7a00]/5" data-testid={`admin-payout-${p.id}`}>
                <td className="p-4 text-[#8a8a8a]">{new Date(p.created_at).toLocaleDateString()}</td>
                <td className="p-4 text-[#cfcfcf]">{p.user_email}</td>
                <td className="p-4 text-right font-display font-bold text-[#ff7a00]">${p.amount.toLocaleString()}</td>
                <td className="p-4 text-[#cfcfcf]">{p.currency}</td>
                <td className="p-4 font-mono text-xs text-[#8a8a8a] max-w-[160px] truncate">{p.wallet_address}</td>
                <td className="p-4">
                  <Badge className={`uppercase text-[10px] tracking-widest ${
                    p.status === "paid" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" :
                    p.status === "approved" ? "bg-[#ff7a00]/15 text-[#ff7a00] border-[#ff7a00]/30" :
                    p.status === "rejected" ? "bg-red-500/15 text-red-400 border-red-500/30" :
                    "bg-yellow-500/15 text-yellow-400 border-yellow-500/30"
                  }`}>{p.status}</Badge>
                </td>
                <td className="p-4 text-right">
                  {p.status === "pending" || p.status === "approved" ? (
                    <Button size="sm" variant="ghost" onClick={() => setDeciding(p)} className="text-[#ff9a2f] hover:text-[#ff7a00]" data-testid={`payout-decide-${p.id}`}>Process</Button>
                  ) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!deciding} onOpenChange={(o) => !o && setDeciding(null)}>
        <DialogContent className="bg-[#0a0a0a] border-[#ff7a00]/30 text-[#f5f5f5]" data-testid="payout-decision-dialog">
          {deciding && (
            <>
              <DialogHeader><DialogTitle className="font-display">Process Payout — ${deciding.amount.toLocaleString()}</DialogTitle></DialogHeader>
              <form onSubmit={decide} className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Decision</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className="bg-[#050505] border-[#ff7a00]/20 h-11 mt-2"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-[#ff7a00]/30 text-[#f5f5f5]">
                      <SelectItem value="approved" className="text-[#f5f5f5]">Approve</SelectItem>
                      <SelectItem value="paid" className="text-[#f5f5f5]">Mark as Paid</SelectItem>
                      <SelectItem value="rejected" className="text-[#f5f5f5]">Reject</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.status === "paid" && (
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Transaction ID / Hash</Label>
                    <Input value={form.tx_id} onChange={(e) => setForm({ ...form, tx_id: e.target.value })} className="bg-[#050505] border-[#ff7a00]/20 h-11 mt-2 font-mono" data-testid="payout-tx-id" />
                  </div>
                )}
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Admin Note (optional)</Label>
                  <Textarea value={form.admin_note} onChange={(e) => setForm({ ...form, admin_note: e.target.value })} className="bg-[#050505] border-[#ff7a00]/20 mt-2" data-testid="payout-admin-note" />
                </div>
                <Button type="submit" disabled={busy} className="w-full h-11 bg-[#ff7a00] hover:bg-[#ff9a2f] text-[#050505] font-semibold" data-testid="payout-decision-submit">
                  {busy ? "Processing..." : "Confirm"}
                </Button>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
